import type {
  PraxaDesktopChannelSummary,
  PraxaDesktopConnectedWorkItem,
  PraxaDesktopConnectedWorkProviderStatus,
  PraxaDesktopConnectedWorkState,
  PraxaDesktopManagedChannel,
  PraxaDesktopResourceIndexState,
} from "@praxa/platform-contracts";

export type PraxaSidebarChannelSource = "praxa" | "slack";
export type PraxaSidebarChannelFilter =
  | "all"
  | "praxa"
  | "slack"
  | "pinned"
  | "hidden"
  | "archived";
export type PraxaSidebarChannelGrouping = "none" | "source";
export type PraxaSidebarChannelSort = "default" | "name" | "recent";

export interface PraxaSidebarChannelPreferences {
  readonly filter: PraxaSidebarChannelFilter;
  readonly grouping: PraxaSidebarChannelGrouping;
  readonly hiddenChannelKeys: readonly string[];
  readonly pinnedPraxaChannelIds: readonly string[];
  readonly sort: PraxaSidebarChannelSort;
}

export interface PraxaSidebarChannelRow {
  readonly archived: boolean;
  readonly canArchive: boolean;
  readonly channelId: string;
  readonly hidden: boolean;
  readonly key: string;
  readonly name: string;
  readonly pinned: boolean;
  readonly position: number | null;
  readonly source: PraxaSidebarChannelSource;
  readonly sourceUrl: string | null;
  readonly updatedAt: string;
}

export interface PraxaSidebarChannelGroup {
  readonly key: PraxaSidebarChannelSource | "all";
  readonly label: string | null;
  readonly rows: readonly PraxaSidebarChannelRow[];
}

interface PraxaSidebarResourceSourceState {
  readonly status: PraxaDesktopResourceIndexState["status"];
  readonly workspaceId: PraxaDesktopResourceIndexState["workspaceId"];
  readonly workspaceKind: PraxaDesktopResourceIndexState["workspaceKind"];
}

interface PraxaSidebarConnectedWorkSourceState {
  readonly status: PraxaDesktopConnectedWorkState["status"];
  readonly workspaceId: PraxaDesktopConnectedWorkState["workspaceId"];
}

export interface PraxaSidebarChannelAvailability {
  readonly activeWorkspaceId: string | null;
  readonly connectedWork: {
    readonly bridgeAvailable: boolean;
    readonly error: boolean;
    readonly loading: boolean;
    readonly slackProviderStatus: PraxaDesktopConnectedWorkProviderStatus | null;
    readonly state: PraxaSidebarConnectedWorkSourceState | null;
    readonly workspaceMismatch: boolean;
  };
  readonly resources: {
    readonly bridgeAvailable: boolean;
    readonly error: boolean;
    readonly loading: boolean;
    readonly state: PraxaSidebarResourceSourceState | null;
  };
  readonly rowCount: number;
}

export interface PraxaSidebarChannelEmptyState {
  readonly live: boolean;
  readonly message: string;
}

const CHANNEL_FILTERS = new Set<PraxaSidebarChannelFilter>([
  "all",
  "praxa",
  "slack",
  "pinned",
  "hidden",
  "archived",
]);
const CHANNEL_GROUPINGS = new Set<PraxaSidebarChannelGrouping>(["none", "source"]);
const CHANNEL_SORTS = new Set<PraxaSidebarChannelSort>(["default", "name", "recent"]);
const MAX_PREFERENCE_IDS = 512;
const MAX_PREFERENCE_ID_LENGTH = 256;

export const DEFAULT_PRAXA_SIDEBAR_CHANNEL_PREFERENCES: PraxaSidebarChannelPreferences = {
  filter: "all",
  grouping: "none",
  hiddenChannelKeys: [],
  pinnedPraxaChannelIds: [],
  sort: "default",
};

function resourcesMismatch(input: PraxaSidebarChannelAvailability): boolean {
  const workspaceId = input.resources.state?.workspaceId;
  return (
    input.activeWorkspaceId !== null &&
    workspaceId !== null &&
    workspaceId !== undefined &&
    workspaceId !== input.activeWorkspaceId
  );
}

export function resolvePraxaSidebarChannelEmptyState(
  input: PraxaSidebarChannelAvailability,
): PraxaSidebarChannelEmptyState | null {
  if (input.rowCount > 0) return null;
  if (input.activeWorkspaceId === null) {
    return {
      live: false,
      message: "Channels are unavailable until a Praxa workspace is selected.",
    };
  }
  if (input.resources.loading || input.connectedWork.loading) {
    return { live: true, message: "Loading channels…" };
  }
  if (!input.resources.bridgeAvailable && !input.connectedWork.bridgeAvailable) {
    return {
      live: false,
      message: "Channels are available in the signed-in desktop app.",
    };
  }

  const resourceStatus = input.resources.state?.status;
  const connectedWorkStatus = input.connectedWork.state?.status;
  if (resourceStatus === "revoked" || connectedWorkStatus === "revoked") {
    return { live: false, message: "Channel access is no longer available." };
  }
  if (resourcesMismatch(input) || input.connectedWork.workspaceMismatch) {
    return {
      live: false,
      message: "Switch to this workspace to view its channels.",
    };
  }
  if (resourceStatus === "unavailable" || connectedWorkStatus === "unavailable") {
    return {
      live: false,
      message: "Channels are unavailable in this desktop session.",
    };
  }
  if (
    input.resources.error ||
    input.connectedWork.error ||
    resourceStatus === "error" ||
    connectedWorkStatus === "error"
  ) {
    return { live: false, message: "Channels couldn’t load." };
  }
  if (input.connectedWork.slackProviderStatus === "error") {
    return {
      live: false,
      message: "Slack channels couldn’t load. Open Work to retry.",
    };
  }
  if (
    resourceStatus === "idle" ||
    resourceStatus === "syncing" ||
    connectedWorkStatus === "idle" ||
    connectedWorkStatus === "syncing"
  ) {
    return { live: true, message: "Loading channels…" };
  }
  if (resourceStatus === "offline" || connectedWorkStatus === "offline") {
    return {
      live: false,
      message: "No saved channels are available offline.",
    };
  }
  if (
    input.connectedWork.slackProviderStatus === "needs_authorization" ||
    input.connectedWork.slackProviderStatus === "permission_lost"
  ) {
    return {
      live: false,
      message: "Reconnect Slack from Work to see its channels.",
    };
  }
  if (
    input.connectedWork.slackProviderStatus === "partial" ||
    input.connectedWork.slackProviderStatus === "rate_limited"
  ) {
    return {
      live: false,
      message: "Slack channels are temporarily incomplete. Open Work for status.",
    };
  }
  return {
    live: false,
    message: "No channels yet. Connect Slack from Work or create a Praxa AI channel.",
  };
}

export function resolvePraxaSidebarChannelSourceWarnings(
  input: PraxaSidebarChannelAvailability,
): readonly string[] {
  if (input.rowCount === 0) return [];
  const warnings: string[] = [];
  const resourceStatus = input.resources.state?.status;
  const connectedWorkStatus = input.connectedWork.state?.status;

  if (input.resources.error || resourceStatus === "error") {
    warnings.push("Praxa AI channels couldn’t refresh.");
  } else if (resourcesMismatch(input)) {
    warnings.push("Praxa AI channels are still switching workspaces.");
  } else if (resourceStatus === "revoked" || resourceStatus === "unavailable") {
    warnings.push("Praxa AI channel access is unavailable.");
  } else if (
    resourceStatus === "offline" &&
    input.resources.state?.workspaceKind === "organization"
  ) {
    warnings.push("Praxa AI channels may be out of date while offline.");
  }

  if (
    input.connectedWork.error ||
    connectedWorkStatus === "error" ||
    input.connectedWork.slackProviderStatus === "error"
  ) {
    warnings.push("Slack channels couldn’t refresh.");
  } else if (input.connectedWork.workspaceMismatch) {
    warnings.push("Slack channels are still switching workspaces.");
  } else if (connectedWorkStatus === "revoked" || connectedWorkStatus === "unavailable") {
    warnings.push("Slack channel access is unavailable.");
  } else if (connectedWorkStatus === "offline") {
    warnings.push("Slack channels may be out of date while offline.");
  } else if (
    input.connectedWork.slackProviderStatus === "needs_authorization" ||
    input.connectedWork.slackProviderStatus === "permission_lost"
  ) {
    warnings.push("Slack needs attention in Work before all channels can load.");
  } else if (
    input.connectedWork.slackProviderStatus === "partial" ||
    input.connectedWork.slackProviderStatus === "rate_limited"
  ) {
    warnings.push("Some Slack channels may be missing while Slack finishes syncing.");
  }

  return warnings;
}

export function praxaSidebarChannelKey(
  source: PraxaSidebarChannelSource,
  channelId: string,
): string {
  return `${source}:${channelId}`;
}

export function praxaSidebarChannelPreferenceStorageKey(workspaceId: string): string {
  return `praxa:sidebar:channels:v1:${encodeURIComponent(workspaceId)}`;
}

function boundedStringArray(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(
      value
        .filter(
          (candidate): candidate is string =>
            typeof candidate === "string" &&
            candidate.length > 0 &&
            candidate.length <= MAX_PREFERENCE_ID_LENGTH,
        )
        .slice(0, MAX_PREFERENCE_IDS),
    ),
  ];
}

export function parsePraxaSidebarChannelPreferences(
  value: unknown,
): PraxaSidebarChannelPreferences {
  if (typeof value !== "object" || value === null) {
    return DEFAULT_PRAXA_SIDEBAR_CHANNEL_PREFERENCES;
  }
  const candidate = value as Record<string, unknown>;
  return {
    filter: CHANNEL_FILTERS.has(candidate.filter as PraxaSidebarChannelFilter)
      ? (candidate.filter as PraxaSidebarChannelFilter)
      : "all",
    grouping: CHANNEL_GROUPINGS.has(candidate.grouping as PraxaSidebarChannelGrouping)
      ? (candidate.grouping as PraxaSidebarChannelGrouping)
      : "none",
    hiddenChannelKeys: boundedStringArray(candidate.hiddenChannelKeys),
    pinnedPraxaChannelIds: boundedStringArray(candidate.pinnedPraxaChannelIds),
    sort: CHANNEL_SORTS.has(candidate.sort as PraxaSidebarChannelSort)
      ? (candidate.sort as PraxaSidebarChannelSort)
      : "default",
  };
}

function compareNames(left: PraxaSidebarChannelRow, right: PraxaSidebarChannelRow): number {
  return left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
}

function compareUpdatedAt(left: PraxaSidebarChannelRow, right: PraxaSidebarChannelRow): number {
  return Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
}

function compareDefault(left: PraxaSidebarChannelRow, right: PraxaSidebarChannelRow): number {
  const pinnedDifference = Number(right.pinned) - Number(left.pinned);
  if (pinnedDifference !== 0) return pinnedDifference;
  if (left.source !== right.source) return left.source === "praxa" ? -1 : 1;
  if (left.source === "praxa" && right.source === "praxa") {
    const positionDifference =
      (left.position ?? Number.MAX_SAFE_INTEGER) - (right.position ?? Number.MAX_SAFE_INTEGER);
    if (positionDifference !== 0) return positionDifference;
  }
  return compareNames(left, right);
}

function matchesFilter(row: PraxaSidebarChannelRow, filter: PraxaSidebarChannelFilter): boolean {
  switch (filter) {
    case "all":
      return !row.hidden && !row.archived;
    case "praxa":
    case "slack":
      return row.source === filter && !row.hidden && !row.archived;
    case "pinned":
      return row.pinned && !row.hidden && !row.archived;
    case "hidden":
      return row.hidden && !row.archived;
    case "archived":
      return row.archived;
  }
}

export function buildPraxaSidebarChannelRows(input: {
  readonly managedChannels: readonly PraxaDesktopManagedChannel[];
  readonly preferences: PraxaSidebarChannelPreferences;
  readonly praxaChannels: readonly PraxaDesktopChannelSummary[];
  readonly slackItems: readonly PraxaDesktopConnectedWorkItem[];
  readonly workspaceId: string;
}): readonly PraxaSidebarChannelRow[] {
  const hiddenKeys = new Set(input.preferences.hiddenChannelKeys);
  const pinnedPraxaIds = new Set(input.preferences.pinnedPraxaChannelIds);
  const managedById = new Map(
    input.managedChannels
      .filter((channel) => channel.workspaceId === input.workspaceId)
      .map((channel) => [channel.channelId, channel] as const),
  );
  const praxaRows = new Map<string, PraxaSidebarChannelRow>();

  for (const channel of input.praxaChannels) {
    if (channel.workspaceId !== input.workspaceId) continue;
    const managed = managedById.get(channel.channelId);
    const key = praxaSidebarChannelKey("praxa", channel.channelId);
    praxaRows.set(channel.channelId, {
      archived: managed?.archivedAt != null,
      canArchive: managed?.canManage === true,
      channelId: channel.channelId,
      hidden: hiddenKeys.has(key),
      key,
      name: channel.name.trim() || "Unnamed channel",
      pinned: pinnedPraxaIds.has(channel.channelId),
      position: channel.position,
      source: "praxa",
      sourceUrl: null,
      updatedAt: channel.updatedAt,
    });
  }

  for (const channel of managedById.values()) {
    if (channel.archivedAt === null || praxaRows.has(channel.channelId)) continue;
    const key = praxaSidebarChannelKey("praxa", channel.channelId);
    praxaRows.set(channel.channelId, {
      archived: true,
      canArchive: channel.canManage,
      channelId: channel.channelId,
      hidden: hiddenKeys.has(key),
      key,
      name: channel.name.trim() || "Unnamed channel",
      pinned: pinnedPraxaIds.has(channel.channelId),
      position: null,
      source: "praxa",
      sourceUrl: null,
      updatedAt: channel.resourceVersion,
    });
  }

  const slackRows = input.slackItems.flatMap((item): readonly PraxaSidebarChannelRow[] => {
    if (
      item.workspaceId !== input.workspaceId ||
      item.provider !== "slack" ||
      item.kind !== "slack_channel"
    ) {
      return [];
    }
    const key = praxaSidebarChannelKey("slack", item.itemId);
    return [
      {
        archived: false,
        canArchive: false,
        channelId: item.itemId,
        hidden: hiddenKeys.has(key),
        key,
        name: item.title.trim() || "Unnamed Slack channel",
        pinned: item.pinned,
        position: null,
        source: "slack",
        sourceUrl: item.sourceUrl,
        updatedAt: item.updatedAt,
      },
    ];
  });

  return [...praxaRows.values(), ...slackRows];
}

export function projectPraxaSidebarChannelGroups(
  rows: readonly PraxaSidebarChannelRow[],
  preferences: Pick<PraxaSidebarChannelPreferences, "filter" | "grouping" | "sort">,
): readonly PraxaSidebarChannelGroup[] {
  const compare =
    preferences.sort === "name"
      ? compareNames
      : preferences.sort === "recent"
        ? compareUpdatedAt
        : compareDefault;
  const visibleRows = rows
    .filter((row) => matchesFilter(row, preferences.filter))
    .toSorted(compare);

  if (preferences.grouping === "none") {
    return [{ key: "all", label: null, rows: visibleRows }];
  }

  return (["praxa", "slack"] as const)
    .map((source) => ({
      key: source,
      label: source === "praxa" ? "Praxa AI" : "Slack",
      rows: visibleRows.filter((row) => row.source === source),
    }))
    .filter((group) => group.rows.length > 0);
}

export function togglePraxaSidebarChannelPreference(
  values: readonly string[],
  value: string,
): readonly string[] {
  return values.includes(value)
    ? values.filter((candidate) => candidate !== value)
    : [...values, value].slice(-MAX_PREFERENCE_IDS);
}
